import { memo, useEffect, useState, type CSSProperties, type SyntheticEvent } from 'react';
import {
  CalendarCheck,
  Check,
  Gift,
  ImageOff,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Upload,
  UserRoundCheck
} from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type {
  AdminCheckinConfig,
  CheckinResponse,
  CheckinReward,
  CheckinRewardDay,
  CheckinRewardQuality
} from '../types';
import { centsToInput, currencyInputToCents, formatMoney } from '../utils';
import {
  Badge,
  Button,
  CollapsiblePanel,
  Field,
  LoadingBlock,
  PageHeader,
  Select,
  TextInput
} from '../components/ui';

const qualityTone: Record<CheckinRewardQuality, string> = {
  gray: '普通',
  green: '优良',
  blue: '稀有',
  purple: '史诗',
  red: '传说',
  gold: '至尊'
};

const qualityColor: Record<CheckinRewardQuality, string> = {
  gray: '#9f927d',
  green: '#6fba2c',
  blue: '#4887c7',
  purple: '#9366c9',
  red: '#e05a5a',
  gold: '#d4a21d'
};

const rewardKinds = [
  { value: 'item', label: '游戏物资' },
  { value: 'currency', label: '网站货币' }
];

const qualityOptions = (Object.keys(qualityTone) as CheckinRewardQuality[]).map(value => ({
  value,
  label: qualityTone[value]
}));

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(monthKey: string) {
  const [year, month] = monthKey.split('-');
  return `${year} 年 ${Number(month)} 月`;
}

function normalizeConfig(config: AdminCheckinConfig): AdminCheckinConfig {
  return {
    ...config,
    rewards: Array.from({ length: 7 }, (_, index) => {
      const day = config.rewards.find(item => item.dayIndex === index + 1);
      return { dayIndex: index + 1, rewards: day?.rewards || [] };
    })
  };
}

function newReward(kind: 'item' | 'currency'): CheckinReward {
  if (kind === 'currency') {
    return { kind, label: '签到货币奖励', icon: '', quality: 'gold', itemId: '', count: 1, amountCents: 10000 };
  }
  return { kind, label: '签到物资奖励', icon: '', quality: 'green', itemId: '', count: 1 };
}

const ItemNameDatalist = memo(function ItemNameDatalist({ items }: { items: Array<{ name: string; id: string }> }) {
  return (
    <datalist id="checkin-item-name-options">
      {items.map(item => <option key={`${item.name}-${item.id}`} value={item.name}>{item.name} · {item.id}</option>)}
    </datalist>
  );
});

function isImageIcon(icon: string) {
  return /^(https?:\/\/|\/|data:image\/)/i.test(icon.trim());
}

function rewardAmount(reward: CheckinReward, currency: string) {
  if (reward.kind === 'currency') return formatMoney(Number(reward.amountCents || 0), currency);
  return `× ${Number(reward.count || 1).toLocaleString('zh-CN')}`;
}

function autoRewardIcon(reward: CheckinReward) {
  const lookupId = reward.kind === 'currency' ? '__coin__' : (reward.itemId.trim() || '__custom__');
  return `/api/checkin/item-icon/${encodeURIComponent(lookupId)}`;
}

async function makeRewardIconData(file: File) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('图片读取失败'));
      element.src = objectUrl;
    });
    for (const size of [96, 80, 64]) {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('浏览器不支持图片处理');
      context.clearRect(0, 0, size, size);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      const scale = Math.min(size / Math.max(1, image.naturalWidth), size / Math.max(1, image.naturalHeight));
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      context.drawImage(image, Math.round((size - width) / 2), Math.round((size - height) / 2), width, height);
      const dataUrl = canvas.toDataURL('image/webp', 0.82);
      if (dataUrl.length <= 30_000) return dataUrl;
    }
    throw new Error('图片压缩后仍然过大，请换一张更简单的图片');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function iconSourceLabel(reward: CheckinReward) {
  if (reward.icon.startsWith('data:image/')) return '当前使用本地上传图片';
  if (isImageIcon(reward.icon)) return '当前使用网络图片';
  if (reward.icon) return '当前使用 Emoji / 文本图标';
  return '当前按物品名称自动匹配';
}

function fallbackRewardImage(event: SyntheticEvent<HTMLImageElement>) {
  const image = event.currentTarget;
  if (image.dataset.fallback === '1') return;
  image.dataset.fallback = '1';
  image.src = '/api/checkin/item-icon/__coin__';
}

function RewardIcon({ reward, large = false }: { reward: CheckinReward; large?: boolean }) {
  const className = 'checkin-item-icon' + (large ? ' large' : '');
  const overrideImage = reward.icon && isImageIcon(reward.icon) ? reward.icon : '';
  const imageSrc = overrideImage || autoRewardIcon(reward);
  if (overrideImage || !reward.icon) {
    return <span className={className}><img src={imageSrc} alt="" loading="lazy" onError={fallbackRewardImage} /></span>;
  }
  return <span className={className}>{reward.icon}</span>;
}

function RewardCard({ day, currency, currentDay, claimedToday }: {
  day: CheckinRewardDay;
  currency: string;
  currentDay: number;
  claimedToday: boolean;
}) {
  const rewards = day.rewards;
  const primary = rewards[0];
  const isSpecial = day.dayIndex === 7;
  const isCurrent = currentDay === day.dayIndex && !claimedToday;
  const isDone = day.dayIndex < currentDay || (claimedToday && day.dayIndex === currentDay);
  const style = {
    '--checkin-quality': primary ? qualityColor[primary.quality] : 'var(--phx-border-light)'
  } as CSSProperties;

  return (
    <article
      className={[
        'checkin-reward-card',
        isSpecial ? 'special' : '',
        isCurrent ? 'current' : '',
        isDone ? 'done' : '',
        rewards.length ? '' : 'empty'
      ].filter(Boolean).join(' ')}
      style={style}
      data-day={day.dayIndex}
    >
      <div className="checkin-card-topline">
        <span className="checkin-day-number">{String(day.dayIndex).padStart(2, '0')}</span>
        {isDone && <span className="checkin-day-done"><Check size={13} /> 已领</span>}
        {isCurrent && <span className="checkin-day-current">今日</span>}
      </div>

      {rewards.length ? (
        <>
          <div className={'checkin-card-icons' + (isSpecial ? ' special-icons' : '')}>
            {rewards.map((reward, index) => (
              <div className="checkin-icon-wrap" key={`${reward.label}-${index}`} title={`${reward.label} ${rewardAmount(reward, currency)}`}>
                <RewardIcon reward={reward} large={isSpecial && rewards.length <= 2} />
              </div>
            ))}
          </div>
          <div className="checkin-card-rewards">
            {isSpecial && <span className="checkin-special-label"><Sparkles size={13} /> 第 7 天专属大奖</span>}
            {rewards.map((reward, index) => (
              <div className="checkin-reward-line" key={`${reward.label}-line-${index}`}>
                <span className="checkin-quality-dot" style={{ background: qualityColor[reward.quality] }} />
                <span className="checkin-reward-name">{reward.label}</span>
                <strong>{rewardAmount(reward, currency)}</strong>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="checkin-card-empty">
          <ImageOff size={25} />
          <span>管理员尚未配置</span>
        </div>
      )}
      <span className="checkin-quality-line" />
    </article>
  );
}

export function CheckinPage({ onNeedAuth, onNavigateProfile }: {
  onNeedAuth: () => void;
  onNavigateProfile: () => void;
}) {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast } = useUi();
  const [data, setData] = useState<CheckinResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [adminMonth, setAdminMonth] = useState(currentMonth);
  const [adminConfig, setAdminConfig] = useState<AdminCheckinConfig | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [itemNameOptions, setItemNameOptions] = useState<Array<{ name: string; id: string }>>([]);
  const [iconUploadKey, setIconUploadKey] = useState<string | null>(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const next = await api<CheckinResponse>('/api/checkin');
      setData(next);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '签到信息加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmin = async (month = adminMonth) => {
    if (!permissions.isStaff) return;
    setAdminLoading(true);
    try {
      const result = await api<{ config: AdminCheckinConfig }>(`/api/admin/checkin?month=${encodeURIComponent(month)}`);
      setAdminConfig(normalizeConfig(result.config));
    } catch (error) {
      setAdminConfig(null);
      toast(error instanceof ApiError ? error.message : '签到配置加载失败', 'error');
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [user?.id]);

  useEffect(() => {
    if (permissions.isStaff) void loadAdmin(adminMonth);
  }, [permissions.isStaff, adminMonth, user?.id]);

  useEffect(() => {
    if (!permissions.isStaff) return;
    let cancelled = false;
    void api<{ total: number; items: Array<{ name: string; id: string }> }>('/api/admin/checkin/item-names')
      .then(result => {
        if (!cancelled) setItemNameOptions(result.items);
      })
      .catch(() => {
        if (!cancelled) setItemNameOptions([]);
      });
    return () => { cancelled = true; };
  }, [permissions.isStaff, user?.id]);

  const claim = async () => {
    if (!user) {
      onNeedAuth();
      return;
    }
    if (!data?.status.bound) {
      toast('请先绑定游戏角色后再领取签到奖励', 'error');
      onNavigateProfile();
      return;
    }
    if (!data.period?.enabled || data.status.claimedToday) return;
    setClaiming(true);
    try {
      const next = await api<CheckinResponse>('/api/checkin/claim', { method: 'POST' });
      setData(next);
      await refresh();
      toast(`第 ${next.status.claim?.dayIndex || ''} 天奖励已领取，游戏物品将直接发放到角色`, 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '签到失败', 'error');
      await load(true);
    } finally {
      setClaiming(false);
    }
  };

  const updateReminder = async (enabled: boolean) => {
    if (!user) {
      onNeedAuth();
      return;
    }
    const previous = data?.status.reminderEnabled || false;
    setData(value => value ? { ...value, status: { ...value.status, reminderEnabled: enabled } } : value);
    setReminderBusy(true);
    try {
      await api('/api/checkin/reminder', { method: 'PATCH', body: JSON.stringify({ enabled }) });
      toast(enabled ? '已开启每日签到提醒' : '已关闭每日签到提醒', 'success');
    } catch (error) {
      setData(value => value ? { ...value, status: { ...value.status, reminderEnabled: previous } } : value);
      toast(error instanceof ApiError ? error.message : '提醒设置保存失败', 'error');
    } finally {
      setReminderBusy(false);
    }
  };

  const updateAdmin = (patch: Partial<AdminCheckinConfig>) => {
    setAdminConfig(value => value ? { ...value, ...patch } : value);
  };

  const updateDayRewards = (dayIndex: number, rewards: CheckinReward[]) => {
    setAdminConfig(value => value ? {
      ...value,
      rewards: value.rewards.map(day => day.dayIndex === dayIndex ? { ...day, rewards } : day)
    } : value);
  };

  const updateReward = (dayIndex: number, rewardIndex: number, patch: Partial<CheckinReward>) => {
    if (!adminConfig) return;
    const day = adminConfig.rewards.find(item => item.dayIndex === dayIndex);
    if (!day) return;
    updateDayRewards(dayIndex, day.rewards.map((reward, index) => index === rewardIndex ? { ...reward, ...patch } : reward));
  };

  const uploadRewardIcon = async (dayIndex: number, rewardIndex: number, file: File | undefined) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast('请选择 PNG、JPG 或 WebP 图片', 'error');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast('原始图片不能超过 8 MB', 'error');
      return;
    }
    const key = `${dayIndex}-${rewardIndex}`;
    setIconUploadKey(key);
    try {
      const icon = await makeRewardIconData(file);
      updateReward(dayIndex, rewardIndex, { icon });
      toast('本地图标已添加，点击保存配置后正式生效', 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : '图标处理失败', 'error');
    } finally {
      setIconUploadKey(null);
    }
  };

  const addReward = (dayIndex: number) => {
    if (!adminConfig) return;
    const day = adminConfig.rewards.find(item => item.dayIndex === dayIndex);
    if (!day || day.rewards.length >= 4) return;
    updateDayRewards(dayIndex, [...day.rewards, newReward('item')]);
  };

  const removeReward = (dayIndex: number, rewardIndex: number) => {
    if (!adminConfig) return;
    const day = adminConfig.rewards.find(item => item.dayIndex === dayIndex);
    if (!day) return;
    updateDayRewards(dayIndex, day.rewards.filter((_, index) => index !== rewardIndex));
  };

  const saveAdmin = async () => {
    if (!adminConfig) return;
    for (const day of adminConfig.rewards) {
      if (day.rewards.length === 0) {
        toast(`请先配置第 ${day.dayIndex} 天的奖励`, 'error');
        return;
      }
      for (const reward of day.rewards) {
        if (!reward.label.trim()) {
          toast(`第 ${day.dayIndex} 天有奖励未填写名称`, 'error');
          return;
        }
        if (reward.kind === 'item' && !reward.itemId.trim()) {
          toast(`第 ${day.dayIndex} 天有物资未填写物品 ID`, 'error');
          return;
        }
        if (reward.kind === 'currency' && !reward.amountCents) {
          toast(`第 ${day.dayIndex} 天有货币奖励金额无效`, 'error');
          return;
        }
      }
    }
    setAdminSaving(true);
    try {
      await api('/api/admin/checkin', {
        method: 'PUT',
        body: JSON.stringify(adminConfig)
      });
      toast(`${monthLabel(adminConfig.monthKey)} 签到配置已保存`, 'success');
      if (adminConfig.monthKey === data?.period?.monthKey || adminConfig.monthKey === currentMonth()) await load(true);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '签到配置保存失败', 'error');
    } finally {
      setAdminSaving(false);
    }
  };

  if (loading && !data) return <LoadingBlock label="正在加载签到活动" />;

  const status = data?.status;
  const period = data?.period;
  const rewards = data?.rewards || [];
  const rewardsReady = rewards.length === 7 && rewards.every(day => day.rewards.length > 0);
  const completedInCycle = status ? (status.claimedToday ? status.currentDayIndex : Math.max(0, status.currentDayIndex - 1)) : 0;
  const canClaim = Boolean(user && status?.bound && period?.enabled && rewardsReady && !status.claimedToday);
  const buttonText = !user
    ? '登录后签到'
    : !status?.bound
      ? '绑定角色后签到'
      : status?.claimedToday
        ? '今日已领取'
        : !period?.enabled
          ? '本月签到暂未开放'
          : !rewardsReady
            ? '奖励配置中'
            : '签到领取奖励';

  return (
    <div className="checkin-page">
      <PageHeader
        title="每日签到"
        description="每月由管理员配置 7 天奖励档位。领取后游戏物品直接发放到绑定角色，网站货币立即到账，无需兑换码。"
        action={<Button variant="ghost" onClick={() => void load()}><RefreshCw size={16} /> 刷新</Button>}
      />

      <section className="checkin-stage" aria-label="每日签到">
        <span className="checkin-deco-line top" />
        <span className="checkin-deco-line bottom" />

        <header className="checkin-hero">
          <div className="checkin-heading">
            <span className="eyebrow">MONTHLY CHECK-IN · {period?.monthKey || currentMonth()}</span>
            <h2>已经 <span>累计签到 <b>{status?.monthlyCount || 0} 天</b></span></h2>
            <p>{!period ? '本月签到活动尚未配置，管理员开放后可领取每日奖励。' : period.enabled ? '每天签到可领取专属奖励，领取后直接发放' : '本月签到活动暂未开放，开放后可领取每日奖励。'}</p>
          </div>
          <div className="checkin-cycle-status">
            <Badge tone={status?.claimedToday ? 'good' : period?.enabled ? 'active' : 'muted'}>
              {status?.claimedToday ? '今日已领取' : period?.enabled ? '今日可签到' : '活动未开放'}
            </Badge>
            <span>{period ? monthLabel(period.monthKey) : monthLabel(currentMonth())}</span>
            <small>本轮进度 {completedInCycle} / 7 天</small>
          </div>
        </header>

        <div className="checkin-reward-grid">
          {Array.from({ length: 7 }, (_, index) => {
            const dayIndex = index + 1;
            const day = rewards.find(item => item.dayIndex === dayIndex) || { dayIndex, rewards: [] };
            return (
              <RewardCard
                key={dayIndex}
                day={day}
                currency={settings.currency}
                currentDay={status?.currentDayIndex || 1}
                claimedToday={Boolean(status?.claimedToday)}
              />
            );
          })}
        </div>

        <div className="checkin-direct-delivery-note">
          <Gift size={18} />
          <div><strong>奖励直接发放</strong><span>游戏物品无需兑换码，将在角色在线时直接进入背包；网站货币领取后立即到账。</span></div>
        </div>

        {!user && (
          <div className="checkin-access-note">
            <UserRoundCheck size={17} />
            <span>游客可以查看奖励，登录并绑定游戏角色后才能领取。</span>
            <Button variant="ghost" onClick={onNeedAuth}>登录 / 注册</Button>
          </div>
        )}
        {user && !status?.bound && (
          <div className="checkin-access-note warn">
            <UserRoundCheck size={17} />
            <span>当前账号尚未绑定游戏角色，奖励暂时无法直接发放。</span>
            <Button variant="ghost" onClick={onNavigateProfile}>前往绑定</Button>
          </div>
        )}

        {status?.claim && (
          <div className="checkin-claim-result">
            <span><Check size={15} /> 今天已领取第 {status.claim.dayIndex} 天奖励</span>
            <div>
              {status.claim.rewards.map((reward, index) => (
                <small key={`${reward.label}-${index}`}>{reward.label} {rewardAmount(reward, settings.currency)}</small>
              ))}
            </div>
            {status.claim.rewards.some(reward => reward.kind === 'item') && <small className="checkin-claim-delivery">游戏物品已加入自动发放队列，将在角色在线时直接进入背包，无需兑换码。</small>}
          </div>
        )}

        <div className="checkin-actions">
          <button
            type="button"
            className={'checkin-claim-button' + (status?.claimedToday ? ' claimed' : '')}
            disabled={claiming || (Boolean(user) && (Boolean(status?.claimedToday) || !period?.enabled || !rewardsReady))}
            onClick={() => void (canClaim || !user || (user && !status?.bound) ? claim() : undefined)}
          >
            {claiming ? <RefreshCw className="spin" size={19} /> : status?.claimedToday ? <Check size={19} /> : <CalendarCheck size={19} />}
            {claiming ? '正在领取…' : buttonText}
          </button>
          <p>{period?.notice || '游戏物品将在角色在线时直接发放到背包，网站货币立即到账，无需兑换码。'}</p>
          <label className={'checkin-reminder-option' + (!user || !status?.bound ? ' disabled' : '')}>
            <input
              type="checkbox"
              checked={Boolean(status?.reminderEnabled)}
              disabled={!user || !status?.bound || reminderBusy}
              onChange={event => void updateReminder(event.target.checked)}
            />
            <span className="checkin-fake-check">{status?.reminderEnabled && <Check size={13} />}</span>
            <span>开启签到领奖提醒，避免错过每天奖励</span>
          </label>
        </div>
      </section>

      {permissions.isStaff && (
        <CollapsiblePanel
          title="本月奖励配置"
          eyebrow="ADMIN · CHECK-IN REWARDS"
          className="top-gap checkin-admin-panel"
          action={<Badge tone="active">管理员可见</Badge>}
        >
          {adminLoading ? <LoadingBlock label="正在加载签到配置" /> : !adminConfig ? (
            <div className="checkin-admin-empty">
              <span>签到配置未能加载。</span>
              <Button onClick={() => void loadAdmin(adminMonth)}>重新加载</Button>
            </div>
          ) : (
            <div className="checkin-admin-editor">
              <ItemNameDatalist items={itemNameOptions} />
              <div className="form-note">
                <Gift size={17} />
                <span>每月独立配置 7 个奖励档位，每天可放 1–4 项物资或网站货币。游戏物资领取后会直接发到绑定角色，不生成兑换码；支持官方中文名称，例如输入“钻石”会自动转换并匹配图标。</span>
              </div>

              <div className="checkin-admin-settings">
                <Field label="配置月份">
                  <TextInput
                    type="month"
                    value={adminMonth}
                    onChange={event => {
                      setAdminMonth(event.target.value || currentMonth());
                      setAdminConfig(null);
                    }}
                  />
                </Field>
                <Field label="活动标题">
                  <TextInput
                    value={adminConfig.title}
                    maxLength={80}
                    onChange={event => updateAdmin({ title: event.target.value })}
                  />
                </Field>
                <Field label="奖励说明">
                  <TextInput
                    value={adminConfig.notice}
                    maxLength={300}
                    onChange={event => updateAdmin({ notice: event.target.value })}
                  />
                </Field>
                <label className="check-line checkin-enable-line">
                  <input
                    type="checkbox"
                    checked={adminConfig.enabled}
                    onChange={event => updateAdmin({ enabled: event.target.checked })}
                  />
                  启用 {monthLabel(adminConfig.monthKey)} 签到活动
                </label>
              </div>

              <div className="checkin-admin-days">
                {adminConfig.rewards.map(day => (
                  <details className="checkin-admin-day" key={day.dayIndex}>
                    <summary>
                      <span className="checkin-admin-day-title">第 {String(day.dayIndex).padStart(2, '0')} 天</span>
                      <span className={day.rewards.length ? 'configured' : 'unconfigured'}>
                        {day.rewards.length ? `${day.rewards.length} 项奖励` : '未配置'}
                      </span>
                    </summary>
                    <div className="checkin-admin-day-body">
                      {day.rewards.map((reward, rewardIndex) => (
                        <div className="checkin-admin-reward" key={`${day.dayIndex}-${rewardIndex}`}>
                          <div className="checkin-admin-reward-head">
                            <div className="checkin-admin-reward-heading">
                              <span className="checkin-admin-reward-preview"><RewardIcon reward={reward} /></span>
                              <span><strong>奖励 {rewardIndex + 1}</strong><small>{reward.icon ? '使用管理员自定义图标' : '按物品 ID 自动匹配图标'}</small></span>
                            </div>
                            <Button variant="danger" onClick={() => removeReward(day.dayIndex, rewardIndex)}><Trash2 size={14} /> 删除</Button>
                          </div>
                          <div className="checkin-admin-reward-grid">
                            <label className="checkin-admin-control">
                              <span>奖励类型</span>
                              <Select
                                value={reward.kind}
                                onChange={event => {
                                  const kind = event.target.value as 'item' | 'currency';
                                  updateReward(day.dayIndex, rewardIndex, kind === 'currency'
                                    ? { kind, amountCents: reward.amountCents || 10000, itemId: '' }
                                    : { kind, itemId: reward.itemId || '', count: reward.count || 1 });
                                }}
                              >
                                {rewardKinds.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                              </Select>
                            </label>
                            <label className="checkin-admin-control">
                              <span>奖励名称</span>
                              <TextInput value={reward.label} maxLength={80} onChange={event => updateReward(day.dayIndex, rewardIndex, { label: event.target.value })} />
                            </label>
                            <div className="checkin-admin-control checkin-icon-control">
                              <span>奖励图标</span>
                              <div className="checkin-icon-upload-row">
                                <label className="button ghost checkin-upload-button">
                                  {iconUploadKey === `${day.dayIndex}-${rewardIndex}` ? <RefreshCw className="spin" size={14} /> : <Upload size={14} />}
                                  {iconUploadKey === `${day.dayIndex}-${rewardIndex}` ? '处理中…' : '从本地上传'}
                                  <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    hidden
                                    disabled={iconUploadKey !== null}
                                    onChange={event => {
                                      const file = event.target.files?.[0];
                                      event.currentTarget.value = '';
                                      void uploadRewardIcon(day.dayIndex, rewardIndex, file);
                                    }}
                                  />
                                </label>
                                {reward.icon && <Button type="button" variant="ghost" onClick={() => updateReward(day.dayIndex, rewardIndex, { icon: '' })}><RefreshCw size={13} /> 恢复自动</Button>}
                              </div>
                              <small className="checkin-admin-hint">{iconSourceLabel(reward)}。支持 PNG、JPG、WebP，最大 8 MB，上传后自动压缩。</small>
                              <details className="checkin-icon-advanced">
                                <summary>使用网络图片地址或 Emoji</summary>
                                <TextInput value={reward.icon.startsWith('data:image/') ? '' : reward.icon} maxLength={30000} placeholder="例如 https://example.com/icon.png 或 🎁" onChange={event => updateReward(day.dayIndex, rewardIndex, { icon: event.target.value })} />
                                <small>图片 URL 是互联网中可以直接打开的图片地址；普通管理员建议直接使用本地上传。</small>
                              </details>
                            </div>
                            <label className="checkin-admin-control">
                              <span>品质</span>
                              <Select value={reward.quality} onChange={event => updateReward(day.dayIndex, rewardIndex, { quality: event.target.value as CheckinRewardQuality })}>
                                {qualityOptions.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                              </Select>
                            </label>
                            {reward.kind === 'item' ? (
                              <>
                                <label className="checkin-admin-control">
                                  <span>物品名称 / 游戏 ID</span>
                                  <TextInput list="checkin-item-name-options" value={reward.itemId} maxLength={120} placeholder="例如 钻石、钻石剑 或 diamond" onChange={event => updateReward(day.dayIndex, rewardIndex, { itemId: event.target.value })} />
                                  <small className="checkin-admin-hint">支持官方中文名称，保存时自动转换为游戏物品 ID{itemNameOptions.length ? `；当前可检索 ${itemNameOptions.length} 个名称` : ''}</small>
                                </label>
                                <label className="checkin-admin-control">
                                  <span>数量</span>
                                  <TextInput type="number" min="1" max="9999" value={reward.count} onChange={event => updateReward(day.dayIndex, rewardIndex, { count: Math.max(1, Number(event.target.value) || 1) })} />
                                </label>
                              </>
                            ) : (
                              <label className="checkin-admin-control">
                                <span>金额（{settings.currency}）</span>
                                <TextInput
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={centsToInput(Number(reward.amountCents || 0))}
                                  onChange={event => updateReward(day.dayIndex, rewardIndex, { amountCents: currencyInputToCents(event.target.value) })}
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button variant="ghost" disabled={day.rewards.length >= 4} onClick={() => addReward(day.dayIndex)}>
                        <Gift size={15} /> 添加奖励（最多 4 项）
                      </Button>
                    </div>
                  </details>
                ))}
              </div>

              <div className="checkin-admin-actions">
                <Button variant="primary" loading={adminSaving} onClick={() => void saveAdmin()}><Save size={16} /> 保存本月签到配置</Button>
                <Button variant="ghost" onClick={() => void loadAdmin(adminMonth)}>重新读取</Button>
              </div>
            </div>
          )}
        </CollapsiblePanel>
      )}
    </div>
  );
}
